import { useRecentLocation } from '@/hooks/useRecentLocation'
import { getAddressAutocomplete } from '@/services'
import { Combobox } from '@headlessui/react'
import { ChangeEvent, forwardRef, useEffect, useRef, useState } from 'react'
import { useDebounce, useOnClickOutside } from 'usehooks-ts'
import { SearchIcon } from '../icons'

export type AutocompleteLocationInputProps = {
  label?: string
  containerClassName?: string
  labelClassName?: string
  inputClassName?: string
  iconClassName?: string
  placeholder?: string
  error?: string
  value: AutocompleteAddress
  setValue: (location: AutocompleteAddress) => void
  onChange?: (location: AutocompleteAddress) => void
  /**
   * Suggest only cities, counties, states, postcodes and countries — never a street address. Set
   * for a person's own location; leave off for venues. The server keeps only the city for a user
   * either way.
   */
  regionsOnly?: boolean
}

export type AutocompleteAddress = {
  description: string
  placeId: string
}

export const AutocompleteLocationInput = forwardRef<any, AutocompleteLocationInputProps>(
  (
    {
      label,
      containerClassName,
      labelClassName,
      inputClassName,
      placeholder = 'Location',
      error,
      setValue,
      iconClassName,
      regionsOnly = false,
      ...props
    },
    ref
  ) => {
    const [searchText, setSearchText] = useState<string>('')
    const [locations, setLocations] = useState<AutocompleteAddress[]>()
    const [isOptionsOpened, setIsOptionsOpened] = useState<boolean>(false)

    const debouncedSearchText = useDebounce<string>(searchText, 200)
    const { recentLocations, addRecentLocation } = useRecentLocation()

    const componentRef = useRef(null)

    const onInput = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim()
      setSearchText(value)
      if (value === '') {
        setValue({ description: '', placeId: '' })
      }
      setIsOptionsOpened(true)
    }

    useEffect(() => {
      const fetchLocations = async () => {
        const res = await getAddressAutocomplete(
          debouncedSearchText,
          '2922eecb-11f9-4d74-8123-ad2fc32078f',
          regionsOnly
        )
        setLocations(
          res.result.map((location: AutocompleteAddress) => {
            return { description: location.description, placeId: location.placeId }
          })
        )
      }
      if (debouncedSearchText) {
        fetchLocations()
      } else {
        // Recent picks can be venues with street addresses, so a region-only field skips them.
        setLocations(!regionsOnly && recentLocations ? recentLocations : [])
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchText])

    useOnClickOutside(componentRef, () => setIsOptionsOpened(false))

    return (
      <div className={`relative flex flex-col gap-y-2 ${containerClassName}`} ref={componentRef}>
        {label && (
          <span className={`text-white text-sm font-semibold ${labelClassName && labelClassName}`}>{label}</span>
        )}
        <div>
          <Combobox ref={ref} {...props}>
            <div className="relative">
              <SearchIcon
                className={`h-7 w-7 absolute top-1/2 -translate-y-1/2 left-4 z-10 pointer-events-none text-navy-900 text-opacity-50 ${
                  iconClassName ? iconClassName : ''
                }`}
              />
              <Combobox.Input
                onInput={onInput}
                onFocus={() => {
                  if (!props.value.description) {
                    setIsOptionsOpened(true)
                  }
                }}
                autoComplete="off"
                className={`p-3 md:p-[17px] rounded-full w-full focus:outline-none text-navy-900 placeholder:text-opacity-50 !pl-12 text-sm
                  border border-navy-900 border-opacity-20 ${inputClassName ? inputClassName : ''}`}
                placeholder={placeholder}
                displayValue={(location: AutocompleteAddress) => location.description}
              />

              {isOptionsOpened && (
                <Combobox.Options
                  static={true}
                  className={'z-20 absolute w-full bg-white mt-1 top-full  shadow-sm shadow-slate-500'}
                >
                  {locations && locations.length > 0 ? (
                    locations.map((location, i) => (
                      <Combobox.Option
                        onClick={() => {
                          setValue(location)
                          addRecentLocation(location)
                          setIsOptionsOpened(false)
                        }}
                        key={`option-key-${i}`}
                        value={location}
                        className={'hover:bg-slate-200 p-2 cursor-pointer'}
                      >
                        {location.description}
                      </Combobox.Option>
                    ))
                  ) : (
                    <p className="p-2 text-slate-500">Nothing Found</p>
                  )}
                </Combobox.Options>
              )}
            </div>
          </Combobox>
        </div>
        {error && <div className="text-boldOrange -mt-1 font-medium">{error}</div>}
      </div>
    )
  }
)
